/**
 * useSelectedTeamRoster Hook
 *
 * Automatically fetches roster data when a team is selected via TeamSelector.
 * Provides roster data, loading state, and team info from context.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSelectedTeam } from '../lib/teamConfig';
import { fetchTeamRoster, fetchNatStatPlayers } from '../utils/natstatDataService';
import { fetchMLBTeamRoster } from '../lib/mlbRosterService';

/**
 * Hook to get roster data for the currently selected team
 * @returns {Object} { roster, loading, error, selectedTeam, refetch }
 */
export function useSelectedTeamRoster() {
  const { selectedTeam } = useSelectedTeam();
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract team details from selection
  const teamId = selectedTeam?.team?.team_id;
  const leagueCode = selectedTeam?.league?.code;
  const teamName = selectedTeam?.team?.team_name;
  const sport = selectedTeam?.sport;

  // Fetch roster for the selected team
  const fetchRoster = useCallback(async () => {
    if (!teamId || !leagueCode) {
      setRoster([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Try to fetch roster filtered by team
      let players = await fetchTeamRoster(leagueCode, teamId);

      // If no players found with team filter, try getting all players for the league
      // This is a fallback for leagues where team_code might not be set
      if (!players || players.length === 0) {
        console.log(`No players found for team ${teamId} in ${leagueCode}, fetching league roster`);
        players = await fetchNatStatPlayers(leagueCode, { limit: 200 });
      }

      // MLB fallback: If still no players and this is MLB, use dynamic mlbRosterService
      // This fetches from local cache (4 teams) OR MLB Stats API (all 30 teams)
      if ((!players || players.length === 0) && leagueCode === 'MLB') {
        console.log(`Trying MLB roster service for ${teamId} (local cache + MLB API fallback)`);
        const mlbRoster = await fetchMLBTeamRoster(teamId);
        if (mlbRoster && mlbRoster.length > 0) {
          // Normalize to expected format
          players = mlbRoster.map(p => ({
            player_id: p.player_id || p.mlbId,
            player_name: p.name || `${p.first_name} ${p.last_name}`,
            position: p.position,
            jersey_number: p.number,
            height: p.height,
            weight: p.weight,
            image: p.image_url || p.photo,
            throws: p.throws,
            bats: p.bats,
            team_name: p.team,
          }));
          console.log(`Loaded ${players.length} players from MLB roster service`);
        }
      }

      setRoster(players);
    } catch (err) {
      console.error('Error fetching roster:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch roster'));
      setRoster([]);
    } finally {
      setLoading(false);
    }
  }, [teamId, leagueCode]);

  // Auto-fetch when team changes
  useEffect(() => {
    if (selectedTeam) {
      fetchRoster();
    }
  }, [selectedTeam, fetchRoster]);

  return {
    roster,
    loading,
    error,
    selectedTeam,
    teamId,
    teamName,
    leagueCode,
    sport,
    refetch: fetchRoster,
    hasTeamSelected: Boolean(selectedTeam),
  };
}

/**
 * Hook to check if a team has been selected
 * @returns {Object} { hasTeamSelected, selectedTeam, teamName, sport }
 */
export function useHasTeamSelected() {
  const { selectedTeam } = useSelectedTeam();

  return {
    hasTeamSelected: Boolean(selectedTeam),
    selectedTeam,
    teamName: selectedTeam?.team?.team_name,
    sport: selectedTeam?.sport,
    leagueCode: selectedTeam?.league?.code,
    leagueName: selectedTeam?.league?.name,
  };
}

export default useSelectedTeamRoster;
